<template>
    <div class="form-edit-page">
        <div class="page-header">
            <div>
                <h1>Gerenciamento de Boletos</h1>
                <p>Lançamento, baixa e consulta de boletos de multas, taxas e serviços.</p>
            </div>
        </div>

        <!-- RESUMO -->
        <div class="summary-grid">
            <div class="summary-card">
                <span class="summary-label">Em aberto</span>
                <strong>{{ resumo.emAberto }}</strong>
            </div>
            <div class="summary-card">
                <span class="summary-label">Pagos</span>
                <strong>{{ resumo.pagos }}</strong>
            </div>
            <div class="summary-card">
                <span class="summary-label">Vencidos</span>
                <strong>{{ resumo.vencidos }}</strong>
            </div>
            <div class="summary-card">
                <span class="summary-label">Valor em aberto</span>
                <strong>{{ formatarMoeda(resumo.valorEmAberto) }}</strong>
            </div>
        </div>

        <!-- FORMULÁRIO -->
        <div class="card-box">
            <div class="card-title">Lançamento / edição de boleto</div>

            <div class="form-grid">
                <div class="form-item col-2">
                    <label>Estabelecimento</label>
                    <input v-model="form.estabelecimento" type="text" placeholder="Nome do estabelecimento" />
                </div>

                <div class="form-item">
                    <label>CNPJ/CPF</label>
                    <input v-model="form.documento" type="text" placeholder="00.000.000/0000-00" />
                </div>

                <div class="form-item">
                    <label>Tipo de cobrança</label>
                    <select v-model="form.tipoCobranca">
                        <option value="">Selecione</option>
                        <option value="Multa">Multa</option>
                        <option value="Taxa de Registro">Taxa de Registro</option>
                        <option value="Taxa de Renovação">Taxa de Renovação</option>
                        <option value="Serviço">Serviço</option>
                        <option value="Outros">Outros</option>
                    </select>
                </div>

                <div class="form-item">
                    <label>Nº Processo / Referência</label>
                    <input v-model="form.referencia" type="text" placeholder="Ex.: PROC-2026-015" />
                </div>

                <div class="form-item">
                    <label>Nº Boleto / Documento</label>
                    <input v-model="form.numeroBoleto" type="text" placeholder="Ex.: BLT-2026-000123" />
                </div>

                <div class="form-item">
                    <label>Data de emissão</label>
                    <input v-model="form.dataEmissao" type="date" />
                </div>

                <div class="form-item">
                    <label>Data de vencimento</label>
                    <input v-model="form.dataVencimento" type="date" />
                </div>

                <div class="form-item">
                    <label>Valor (R$)</label>
                    <input v-model="form.valor" type="number" step="0.01" placeholder="0,00" />
                </div>

                <div class="form-item">
                    <label>Situação</label>
                    <select v-model="form.status">
                        <option value="EM_ABERTO">Em aberto</option>
                        <option value="PAGO">Pago</option>
                        <option value="VENCIDO">Vencido</option>
                        <option value="CANCELADO">Cancelado</option>
                    </select>
                </div>

                <div class="form-item" v-if="form.status === 'PAGO'">
                    <label>Data de pagamento</label>
                    <input v-model="form.dataPagamento" type="date" />
                </div>

                <div class="form-item col-4">
                    <label>Observações</label>
                    <textarea
                        v-model="form.observacoes"
                        rows="3"
                        placeholder="Informações adicionais sobre a cobrança, pagamento ou baixa..."
                    ></textarea>
                </div>
            </div>

            <div class="form-actions">
                <button class="btn btn-primary" @click="salvarBoleto">
                    {{ form.id ? 'Atualizar' : 'Lançar boleto' }}
                </button>
                <button class="btn btn-secondary" @click="limparForm">Limpar</button>
            </div>
        </div>

        <!-- FILTROS -->
        <div class="card-box">
            <div class="card-title">Consulta de boletos</div>

            <div class="form-grid">
                <div class="form-item col-2">
                    <label>Pesquisar</label>
                    <input
                        v-model="filtros.termo"
                        type="text"
                        placeholder="Estabelecimento, processo ou número do boleto"
                    />
                </div>

                <div class="form-item">
                    <label>Tipo</label>
                    <select v-model="filtros.tipo">
                        <option value="">Todos</option>
                        <option value="Multa">Multa</option>
                        <option value="Taxa de Registro">Taxa de Registro</option>
                        <option value="Taxa de Renovação">Taxa de Renovação</option>
                        <option value="Serviço">Serviço</option>
                        <option value="Outros">Outros</option>
                    </select>
                </div>

                <div class="form-item">
                    <label>Situação</label>
                    <select v-model="filtros.status">
                        <option value="">Todas</option>
                        <option value="EM_ABERTO">Em aberto</option>
                        <option value="PAGO">Pago</option>
                        <option value="VENCIDO">Vencido</option>
                        <option value="CANCELADO">Cancelado</option>
                    </select>
                </div>

                <div class="form-item">
                    <label>Vencimento inicial</label>
                    <input v-model="filtros.dtIni" type="date" />
                </div>

                <div class="form-item">
                    <label>Vencimento final</label>
                    <input v-model="filtros.dtFim" type="date" />
                </div>
            </div>
        </div>

        <!-- TABELA -->
        <div class="card-box">
            <div class="card-title">Boletos cadastrados</div>

            <div class="table-wrap">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Estabelecimento</th>
                            <th>Tipo</th>
                            <th>Processo</th>
                            <th>Nº boleto</th>
                            <th>Vencimento</th>
                            <th>Valor</th>
                            <th>Situação</th>
                            <th>Pagamento</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in boletosFiltrados" :key="item.id">
                            <td>
                                <div class="table-main">{{ item.estabelecimento }}</div>
                                <div class="table-sub">{{ item.documento }}</div>
                            </td>
                            <td>{{ item.tipoCobranca }}</td>
                            <td>{{ item.referencia }}</td>
                            <td>{{ item.numeroBoleto }}</td>
                            <td>{{ formatarDataBR(item.dataVencimento) }}</td>
                            <td>{{ formatarMoeda(item.valor) }}</td>
                            <td>
                                <span class="status-badge" :class="badgeClass(item.status)">
                                    {{ labelStatus(item.status) }}
                                </span>
                            </td>
                            <td>{{ item.dataPagamento ? formatarDataBR(item.dataPagamento) : '-' }}</td>
                            <td>
                                <div class="table-actions">
                                    <button class="btn-table" @click="editarBoleto(item)">Editar</button>
                                    <button
                                        class="btn-table success"
                                        @click="baixarBoleto(item)"
                                        v-if="item.status !== 'PAGO' && item.status !== 'CANCELADO'"
                                    >
                                        Baixar
                                    </button>
                                    <button
                                        class="btn-table danger"
                                        @click="cancelarBoleto(item)"
                                        v-if="item.status !== 'CANCELADO'"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </td>
                        </tr>

                        <tr v-if="!boletosFiltrados.length">
                            <td colspan="9" class="empty-state">
                                Nenhum boleto encontrado para os filtros informados.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';

const boletos = ref([
    {
        id: 1,
        estabelecimento: 'Frigorífico Bom Corte Ltda',
        documento: '12.345.678/0001-90',
        tipoCobranca: 'Multa',
        referencia: 'PROC-2026-015',
        numeroBoleto: 'BLT-2026-000123',
        dataEmissao: '2026-03-01',
        dataVencimento: '2026-03-20',
        valor: 1250.50,
        status: 'EM_ABERTO',
        dataPagamento: '',
        observacoes: 'Multa por não conformidade em inspeção.'
    },
    {
        id: 2,
        estabelecimento: 'Laticínios Vale do Sul',
        documento: '04.222.111/0001-44',
        tipoCobranca: 'Taxa de Registro',
        referencia: 'REG-2026-004',
        numeroBoleto: 'BLT-2026-000124',
        dataEmissao: '2026-02-10',
        dataVencimento: '2026-02-28',
        valor: 480.00,
        status: 'PAGO',
        dataPagamento: '2026-02-26',
        observacoes: 'Taxa referente ao registro inicial.'
    },
    {
        id: 3,
        estabelecimento: 'Entreposto Colonial Serra Alta',
        documento: '77.888.999/0001-10',
        tipoCobranca: 'Serviço',
        referencia: 'SERV-2026-008',
        numeroBoleto: 'BLT-2026-000125',
        dataEmissao: '2026-02-05',
        dataVencimento: '2026-02-15',
        valor: 320.00,
        status: 'VENCIDO',
        dataPagamento: '',
        observacoes: 'Cobrança de serviço complementar.'
    }
]);

const formPadrao = () => ({
    id: null,
    estabelecimento: '',
    documento: '',
    tipoCobranca: '',
    referencia: '',
    numeroBoleto: '',
    dataEmissao: '',
    dataVencimento: '',
    valor: '',
    status: 'EM_ABERTO',
    dataPagamento: '',
    observacoes: ''
});

const form = reactive(formPadrao());

const filtros = reactive({
    termo: '',
    tipo: '',
    status: '',
    dtIni: '',
    dtFim: ''
});

const resumo = computed(() => {
    const emAberto = boletos.value.filter(x => x.status === 'EM_ABERTO').length;
    const pagos = boletos.value.filter(x => x.status === 'PAGO').length;
    const vencidos = boletos.value.filter(x => x.status === 'VENCIDO').length;

    const valorEmAberto = boletos.value
        .filter(x => x.status === 'EM_ABERTO' || x.status === 'VENCIDO')
        .reduce((acc, item) => acc + Number(item.valor || 0), 0);

    return {
        emAberto,
        pagos,
        vencidos,
        valorEmAberto
    };
});

const boletosFiltrados = computed(() => {
    return boletos.value.filter(item => {
        const termo = filtros.termo.toLowerCase();

        const matchTermo =
            !filtros.termo ||
            item.estabelecimento.toLowerCase().includes(termo) ||
            item.referencia.toLowerCase().includes(termo) ||
            item.numeroBoleto.toLowerCase().includes(termo);

        const matchTipo = !filtros.tipo || item.tipoCobranca === filtros.tipo;
        const matchStatus = !filtros.status || item.status === filtros.status;
        const matchDtIni = !filtros.dtIni || item.dataVencimento >= filtros.dtIni;
        const matchDtFim = !filtros.dtFim || item.dataVencimento <= filtros.dtFim;

        return matchTermo && matchTipo && matchStatus && matchDtIni && matchDtFim;
    });
});

function salvarBoleto() {
    if (!form.estabelecimento || !form.tipoCobranca || !form.dataVencimento || !form.valor) {
        alert('Preencha os campos obrigatórios do boleto.');
        return;
    }

    if (form.id) {
        const idx = boletos.value.findIndex(x => x.id === form.id);
        if (idx >= 0) {
            boletos.value[idx] = {
                ...boletos.value[idx],
                ...JSON.parse(JSON.stringify(form)),
                valor: Number(form.valor)
            };
        }
    } else {
        boletos.value.unshift({
            ...JSON.parse(JSON.stringify(form)),
            id: Date.now(),
            valor: Number(form.valor)
        });
    }

    limparForm();
}

function limparForm() {
    Object.assign(form, formPadrao());
}

function editarBoleto(item) {
    Object.assign(form, JSON.parse(JSON.stringify(item)));
}

function baixarBoleto(item) {
    item.status = 'PAGO';
    item.dataPagamento = new Date().toISOString().slice(0, 10);
}

function cancelarBoleto(item) {
    item.status = 'CANCELADO';
}

function labelStatus(status) {
    switch (status) {
        case 'EM_ABERTO':
            return 'Em aberto';
        case 'PAGO':
            return 'Pago';
        case 'VENCIDO':
            return 'Vencido';
        case 'CANCELADO':
            return 'Cancelado';
        default:
            return status;
    }
}

function badgeClass(status) {
    return {
        aberto: status === 'EM_ABERTO',
        pago: status === 'PAGO',
        vencido: status === 'VENCIDO',
        cancelado: status === 'CANCELADO'
    };
}

function formatarMoeda(valor) {
    return Number(valor || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    });
}

function formatarDataBR(data) {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
}
</script>

<style scoped>
.form-edit-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background: #f5f7fb;
}

.page-header h1 {
    margin: 0;
    font-size: 24px;
    color: #1f2937;
}

.page-header p {
    margin: 4px 0 0 0;
    color: #6b7280;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.summary-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.summary-label {
    font-size: 12px;
    color: #6b7280;
    text-transform: uppercase;
}

.summary-card strong {
    font-size: 22px;
    color: #111827;
}

.card-box {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 14px;
    color: #1f2937;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
}

.form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-item.col-2 {
    grid-column: span 2;
}

.form-item.col-4 {
    grid-column: span 4;
}

.form-item label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}

.form-item input,
.form-item select,
.form-item textarea {
    width: 100%;
    min-height: 40px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    background: #fff;
    box-sizing: border-box;
}

.form-item textarea {
    min-height: 90px;
    resize: vertical;
}

.form-actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
}

.btn {
    border: none;
    border-radius: 8px;
    padding: 10px 14px;
    cursor: pointer;
    font-weight: 600;
}

.btn-primary {
    background: #2563eb;
    color: #fff;
}

.btn-secondary {
    background: #e5e7eb;
    color: #111827;
}

.table-wrap {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table th {
    text-align: left;
    padding: 12px 10px;
    background: #f3f4f6;
    color: #374151;
    font-size: 13px;
}

.data-table td {
    padding: 12px 10px;
    border-top: 1px solid #e5e7eb;
    vertical-align: top;
    font-size: 14px;
    color: #111827;
}

.table-main {
    font-weight: 600;
}

.table-sub {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
}

.status-badge {
    display: inline-block;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.status-badge.aberto {
    background: #dbeafe;
    color: #1d4ed8;
}

.status-badge.pago {
    background: #dcfce7;
    color: #166534;
}

.status-badge.vencido {
    background: #fee2e2;
    color: #b91c1c;
}

.status-badge.cancelado {
    background: #e5e7eb;
    color: #4b5563;
}

.table-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.btn-table {
    border: 1px solid #d1d5db;
    background: #fff;
    border-radius: 6px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 12px;
}

.btn-table.success {
    border-color: #86efac;
    color: #166534;
}

.btn-table.danger {
    border-color: #fecaca;
    color: #b91c1c;
}

.empty-state {
    text-align: center;
    color: #6b7280;
    padding: 18px;
}

@media (max-width: 1100px) {
    .summary-grid,
    .form-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .form-item.col-4 {
        grid-column: span 2;
    }
}

@media (max-width: 700px) {
    .summary-grid,
    .form-grid {
        grid-template-columns: 1fr;
    }

    .form-item.col-2,
    .form-item.col-4 {
        grid-column: span 1;
    }
}
</style>